import { PolymerElement, html } from 'polymer';

/**
 * Manage which element will show
 *
 * ```html
 * <hi-switch value="B">
 *   <hi-case value="A"></hi-case>
 *   <hi-case value="B"></hi-case>
 * </hi-switch>
 * ```
 *
 * `<hi-switch>` does not set `[role]` or add assistive properties because it manages the visibility of a set of tags. Only the visible tag will be provided to assistive technologies. The user should add `[role]` properties to the switch and case based on how those tags are used in their application. For instance:
 *
 * ```html
 * <hi-switch role="main">
 *   <hi-case role="section"></hi-case>
 *   <hi-case role="section"></hi-case>
 * </hi-switch>
 * ```
 *
 * Any tag can be used to show/hide.
 *
 * ```html
 * <hi-switch>
 *   <li value="one"></li>
 *   <li value="two"></li>
 * </hi-switch>
 * ```
 *
 * The two attributes used are `value` for the value of the tag and `selected` to indicate selection.
 *
 * @demo demo/index.html
 * @customElement
 * @polymer
 * @appliesMixin HIP.BehaviorElement
 */
class SwitchElement extends HIP.BehaviorContainer(HIP.BehaviorElement(PolymerElement)) {
  static get is() {
    return 'hi-switch';
  }

  static get template() {
    return html`
      <style is="custom-style" include="hi-theme"></style>
      <style>
        :host {
          display: block;
        }
      </style>
      <slot id="items"></slot>
    `;
  }

  static get properties() {
    return {
      /**
       *
       * using a zero-based index
       */
      selectedIndex: {
        type: Number,
        reflectToAttribute: true,
        notify: true
      },
      /**
       *
       * set the string of a `<hi-case>` value to match against. `selectedIndex` will override `value`
       */
      value: {
        type: String,
        notify: true
      },
      valueAttr: {
        type: String,
        value: 'value'
      }
    };
  }

  static get observers() {
    return ['__selectedAnyChanged(selectedIndex, value)'];
  }

  static get autoBinding() {
    return {
      signal: 'selected-index-changed',
      slot: 'selectedIndex'
    };
  }

  /**
   *
   *
   */
  __onSlotChanged() {
    this.refresh();
    this.__updatedChildren(this.selectedIndex, this.value);
  }

  __selectedAnyChanged(selectedIndex, value) {
    this.__updatedChildren(selectedIndex, value);
  }

  /**
   *
   * will update against (in order) selectedIndex, value.
   * if value is set, then a value attribute should be set on each `<hi-case>`
   */
  __updatedChildren(selectedIndex, value) {
    let compareBy;

    if (selectedIndex !== undefined) {
      compareBy = 'index';
    } else if (value !== undefined) {
      compareBy = 'value';
    } else {
      compareBy = 'none';
    }

    this.slotChildren.forEach((element, index) => {
      const hasValidValue =
        (compareBy === 'value' && element.getAttribute(this.valueAttr) === value) ||
        (compareBy === 'index' && index === selectedIndex);
      const hasValidChange = compareBy !== 'none';
      const willShow = hasValidChange && hasValidValue;

      element.style.display = willShow ? 'initial' : 'none';
    });
  }
}

HIP.factory.element(SwitchElement);
